import { useState, useCallback, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { supabase } from '../lib/supabase';
import { NAVY, GOLD, PAYMENT_MODES } from '../utils/constants';
import { buildRevenueBlocks, blockRowsForExport } from '../utils/revenueBlocks';
import RevenueBlockTable from './ui/RevenueBlockTable';
import { useCruiseSettings } from '../hooks/useCruiseSettings';
import { Lock, Eye, EyeOff } from 'lucide-react';

// Session-scoped auth — resets on page refresh, survives React re-renders
let rangeReportsAuthed = false;

// ─── Date helpers ─────────────────────────────────────────────────────────────
const todayInput = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};
const toDisplayDate = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const [y, m, d] = yyyymmdd.split('-');
  return `${d}-${m}-${y}`;
};
const MONTHS_LONG = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const ordinalSuffix = (n) => { const s = ['th','st','nd','rd'], v = n % 100; return n + (s[(v-20)%10] || s[v] || s[0]); };
const formatDateForHeader = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  return `${ordinalSuffix(d)} ${MONTHS_LONG[m-1]} ${y}`;
};
const addDays = (yyyymmdd, n) => {
  const d = new Date(yyyymmdd + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const getMondayOf = (yyyymmdd) => {
  const d = new Date(yyyymmdd + 'T00:00:00');
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const daysBetween = (from, to) =>
  Math.round((new Date(to + 'T00:00:00') - new Date(from + 'T00:00:00')) / 86400000);

const buildPresets = () => {
  const today = todayInput();
  const yesterday = addDays(today, -1);
  const thisMonday = getMondayOf(today);
  const lastMonday = addDays(thisMonday, -7);
  const lastSunday = addDays(thisMonday, -1);
  const [ty, tm] = today.split('-');
  const firstOfMonth = `${ty}-${tm}-01`;
  const lastMonthEnd = addDays(firstOfMonth, -1);
  const [lmy, lmm] = lastMonthEnd.split('-');
  const firstOfLastMonth = `${lmy}-${lmm}-01`;
  return [
    { key: 'today',      label: 'Today',      from: today,          to: today },
    { key: 'yesterday',  label: 'Yesterday',  from: yesterday,      to: yesterday },
    { key: 'thisWeek',   label: 'This Week',  from: thisMonday,     to: today },
    { key: 'lastWeek',   label: 'Last Week',  from: lastMonday,     to: lastSunday },
    { key: 'thisMonth',  label: 'This Month', from: firstOfMonth,   to: today },
    { key: 'lastMonth',  label: 'Last Month', from: firstOfLastMonth, to: lastMonthEnd },
    { key: 'custom',     label: 'Custom',     from: null,           to: null },
  ];
};

const CRUISE_ORDER_FALLBACK = ['Sunset Cruise', 'Sundown Cruise', 'Dinner Cruise'];
const PAYMENT_MODE_COLS = {
  'Razorpay':      'razorpay_amount',
  'Cash':          'cash_amount',
  'ICICI POS':     'icici_amount',
  'Bank Transfer': 'bank_transfer_amount',
  'GPay':          'gpay_amount',
};
const fmt = (n) => (n || 0).toLocaleString('en-IN');
const fmtAmount = (n) => `₹${fmt(n)}`;

// Every DD-MM-YYYY date from fromISO to toISO inclusive
const datesBetween = (fromISO, toISO) => {
  const out = [];
  const cur = new Date(fromISO + 'T00:00:00');
  const end = new Date(toISO + 'T00:00:00');
  while (cur <= end) {
    out.push(`${String(cur.getDate()).padStart(2, '0')}-${String(cur.getMonth() + 1).padStart(2, '0')}-${cur.getFullYear()}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
};
const ddmmyyyyToISO = (t) => (t && /^\d{2}-\d{2}-\d{4}$/.test(t)) ? t.split('-').reverse().join('-') : '9999';
const paidOf = (b) => PAYMENT_MODES.reduce((s, m) => s + (Number(b[PAYMENT_MODE_COLS[m]]) || 0), 0);

// Days since the cruise date for a Balance booking (DD-MM-YYYY) — 0 = today
const balanceDaysPending = (dateStr) => {
  const [d, m, yr] = (dateStr || '').split('-').map(Number);
  const cruiseDate = new Date(yr, m - 1, d);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today - cruiseDate) / 86400000));
};
const balanceDaysLabel = (n) => (n === 0 ? 'Today' : `${n}d`);

// ─── Styles ───────────────────────────────────────────────────────────────────
const card = { background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14, padding: '18px 22px', marginBottom: 18, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' };
const sectionLabel = { fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 14 };
const thStyle = { padding: '8px 12px', fontWeight: 700, fontSize: 'var(--text-xs)', color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--color-sunken)', borderBottom: '1px solid var(--color-border-strong)' };
const tdStyle = { padding: '8px 12px', fontSize: 'var(--text-sm)', color: 'var(--color-ink-brand-softer)', borderBottom: '1px solid var(--color-border-subtle)', verticalAlign: 'middle' };
const tdNumStyle = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 };
const tfootStyle = { padding: '9px 12px', fontWeight: 700, fontSize: 'var(--text-sm)', color: 'var(--color-ink-brand)', textAlign: 'right', background: 'var(--color-sunken)', borderTop: '1px solid var(--color-border-strong)', fontVariantNumeric: 'tabular-nums' };
const tableWrap = { overflowX: 'auto', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', marginBottom: 10 };
const tblStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' };

function SectionTitle({ title }) {
  return <div style={sectionLabel}>{title}</div>;
}

// ─── Password Modal ────────────────────────────────────────────────────────────
function PasswordModal({ onAuth, onCancel }) {
  const [pw, setPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!pw) { setError('Please enter the password'); return; }
    setLoading(true);
    setError('');
    const { data } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'range_reports_password')
      .single();
    if (data?.value === pw) {
      rangeReportsAuthed = true;
      onAuth();
    } else {
      setError('Incorrect password');
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 16px' }}>
      <div style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 18, padding: '40px 36px', boxShadow: '0 8px 40px rgba(10,22,40,0.12)', maxWidth: 380, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ marginBottom: 8 }}><Lock size={32} strokeWidth={1.75} aria-hidden="true" /></div>
          <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: NAVY, margin: 0, marginBottom: 6 }}>Range Reports</h3>
          <p style={{ fontSize: '0.82rem', color: 'var(--color-ink-muted)', margin: 0 }}>Enter password to continue</p>
        </div>
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <input
            type={showPw ? 'text' : 'password'}
            value={pw}
            onChange={e => setPw(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !loading && handleSubmit()}
            placeholder="Password"
            autoFocus
            style={{ width: '100%', boxSizing: 'border-box', background: 'var(--color-sunken)', border: `1.5px solid ${error ? '#dc2626' : 'var(--color-border-strong)'}`, borderRadius: 9, padding: '11px 44px 11px 14px', color: NAVY, fontSize: '0.92rem', outline: 'none' }}
          />
          <button
            onClick={() => setShowPw(v => !v)}
            style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-ink-muted)', display: 'flex', padding: 0, lineHeight: 1 }}
            tabIndex={-1}
            aria-label={showPw ? 'Hide password' : 'Show password'}
          >
            {showPw ? <EyeOff size={18} strokeWidth={1.75} aria-hidden="true" /> : <Eye size={18} strokeWidth={1.75} aria-hidden="true" />}
          </button>
        </div>
        {error && <div style={{ fontSize: '0.78rem', color: '#dc2626', fontWeight: 600, marginBottom: 14 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{ flex: 1, padding: '11px 0', background: loading ? '#ccc' : NAVY, color: '#fff', border: 'none', borderRadius: 9, fontSize: '0.9rem', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}
          >
            {loading ? 'Checking…' : 'Continue'}
          </button>
          <button
            onClick={onCancel}
            style={{ flex: 1, padding: '11px 0', background: '#fff', color: 'var(--color-ink-secondary)', border: '1.5px solid var(--color-border-strong)', borderRadius: 9, fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────
export default function RangeReports({ onNavigate }) {
  const [authed, setAuthed] = useState(rangeReportsAuthed);
  if (!authed) {
    return (
      <div>
        <div className="back-row">
          <button className="back-btn" onClick={() => onNavigate('operations')}>← Operations</button>
        </div>
        <PasswordModal onAuth={() => setAuthed(true)} onCancel={() => onNavigate('operations')} />
      </div>
    );
  }
  return <RangeReportsContent onNavigate={onNavigate} />;
}

// ─── Content (shown after auth) ───────────────────────────────────────────────
function RangeReportsContent({ onNavigate }) {
  const { settings: cruiseSettings } = useCruiseSettings();
  const CRUISE_ORDER = cruiseSettings.length > 0 ? cruiseSettings.map(s => s.cruise_name) : CRUISE_ORDER_FALLBACK;

  const PRESETS = buildPresets();
  const [activePreset, setActivePreset] = useState('today');
  const [fromDate, setFromDate] = useState(todayInput());
  const [toDate, setToDate] = useState(todayInput());
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [balanceBookings, setBalanceBookings] = useState([]);
  // Unpaid remainder across every sailed cruise — the full picture, one figure,
  // with the Partner Ledger a click away. Kept out of the period table.
  const [allDatesOutstanding, setAllDatesOutstanding] = useState(0);

  const rangeLen = fromDate && toDate ? daysBetween(fromDate, toDate) : 0;
  const rangeExceeds31 = rangeLen > 30;
  const rangeValid = fromDate && toDate && fromDate <= toDate && !rangeExceeds31;

  // Balance (unpaid) — bookings on the generated report's dates still carrying an unpaid
  // remainder, whatever their status: the "Balance (unpaid)" line of the revenue block,
  // itemised. Real-time so a payment recorded in the tracker drops off at once.
  const reportFrom = report?.fromDate, reportTo = report?.toDate;
  const fetchBalanceBookings = useCallback(async () => {
    const BAL_COLS = 'id, booking_name, partner_name, date, cruise_type, deck, adults, total_amount, tac, payment_status, razorpay_amount, cash_amount, icici_amount, bank_transfer_amount, gpay_amount';
    const withRemaining = (b) => {
      const net = (Number(b.total_amount) || 0) - (Number(b.tac) || 0);
      const paid = paidOf(b);
      return { ...b, net, paid, remaining: net - paid };
    };

    // All-dates figure: Balance-status bookings, any date, remainder summed — the same
    // rule as the Partner Ledger and the Home Balance card, so the three never disagree
    // (the ledger is the partner subset of this).
    const { data: allData } = await supabase
      .from('bookings')
      .select(BAL_COLS)
      .eq('payment_status', 'Balance')
      .limit(2000);
    setAllDatesOutstanding((allData || []).map(withRemaining).reduce((s, b) => s + b.remaining, 0));

    if (!reportFrom || !reportTo) { setBalanceBookings([]); return; }
    const { data } = await supabase
      .from('bookings')
      .select(BAL_COLS)
      .in('date', datesBetween(reportFrom, reportTo))
      .not('payment_status', 'in', '("FOC","Cancelled")')
      .limit(2000);
    setBalanceBookings((data || [])
      .map(withRemaining)
      .filter(b => b.remaining > 0.5)
      .sort((a, b) => ddmmyyyyToISO(a.date).localeCompare(ddmmyyyyToISO(b.date)) || b.remaining - a.remaining));
  }, [reportFrom, reportTo]);

  useEffect(() => {
    fetchBalanceBookings();
    const sub = supabase
      .channel('range-reports-balance')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bookings' }, fetchBalanceBookings)
      .subscribe();
    return () => supabase.removeChannel(sub);
  }, [fetchBalanceBookings]);

  // ─── Data fetch ─────────────────────────────────────────────────────────────
  const generateReport = useCallback(async (from, to) => {
    if (!from || !to || from > to) return;
    setLoading(true);
    setReport(null);

    try {
      const COLS = 'id, adults, children, tour_leaders, total_amount, tac, net_total, payment_status, payment_mode, partner_name, cruise_type, deck, booking_name, date, razorpay_amount, cash_amount, icici_amount, bank_transfer_amount, gpay_amount, is_refunded, refund_amount, refund_mode';

      // DD-MM-YYYY date array; fetch each day in parallel
      const dateArray = datesBetween(from, to);

      const bookingResults = await Promise.all(
        dateArray.map(date =>
          supabase.from('bookings').select(COLS).eq('date', date).limit(500)
        )
      );
      const allBookings = bookingResults.flatMap(r => r.data || []);
      const bookingIds = allBookings.map(b => b.id);

      // Chunk to avoid PostgREST URL length limits (~23KB for 620 UUIDs)
      let trackerEntries = [];
      if (bookingIds.length > 0) {
        const CHUNK = 100;
        const chunks = [];
        for (let i = 0; i < bookingIds.length; i += CHUNK) chunks.push(bookingIds.slice(i, i + CHUNK));
        const teResults = await Promise.all(
          chunks.map(ids =>
            supabase.from('tracker_entries')
              .select('booking_id, driver_commission')
              .in('booking_id', ids)
              .limit(500)
          )
        );
        trackerEntries = teResults.flatMap(r => r.data || []);
      }

      const { data: adjData } = await supabase
        .from('daily_adjustments')
        .select('*')
        .gte('date', from)
        .lte('date', to)
        .order('date', { ascending: true });

      const pax = (b) => (b.adults || 0) + (b.children || 0);
      // revenueActive: non-FOC, non-Cancelled — gross/TAC stats (billed side)
      // modeActive: non-FOC — the RECEIPTS row set. Mode columns record money actually received
      //   (net of TAC at source), so Net Collection, the payment-mode breakdown, cash accounting
      //   AND refunds all come from this one population (see Reports.js for the full note).
      const revenueActive  = allBookings.filter(b => b.payment_status !== 'FOC' && b.payment_status !== 'Cancelled');
      const modeActive      = allBookings.filter(b => b.payment_status !== 'FOC');
      const boardingActive  = allBookings.filter(b => !['Cancelled', 'FOC'].includes(b.payment_status));

      // Pax summary
      const paxSummaryMap = {};
      boardingActive.forEach(b => {
        const key = `${b.cruise_type}||${b.deck}`;
        if (!paxSummaryMap[key]) paxSummaryMap[key] = { cruise: b.cruise_type, deck: b.deck, bookings: 0, adults: 0, children: 0, tl: 0, pax: 0 };
        paxSummaryMap[key].bookings += 1;
        paxSummaryMap[key].adults   += b.adults || 0;
        paxSummaryMap[key].children += b.children || 0;
        paxSummaryMap[key].tl       += b.tour_leaders || 0;
        paxSummaryMap[key].pax      += pax(b);
      });
      const paxSummary = Object.values(paxSummaryMap).sort((a, b) => {
        const ci = CRUISE_ORDER.indexOf(a.cruise) - CRUISE_ORDER.indexOf(b.cruise);
        return ci !== 0 ? ci : a.deck.localeCompare(b.deck);
      });

      // Revenue
      const gross          = revenueActive.reduce((s, b) => s + (b.total_amount || 0), 0);
      const totalTAC       = revenueActive.reduce((s, b) => s + (b.tac || 0), 0);
      // Payment mode breakdown — direct column sums only, no fallback
      const byMode = {};
      PAYMENT_MODES.forEach(m => { byMode[m] = 0; });
      modeActive.forEach(b => {
        PAYMENT_MODES.forEach(m => { byMode[m] += b[PAYMENT_MODE_COLS[m]] || 0; });
      });
      // Payments Received — every rupee recorded in a mode column on the receipts row set
      const receivedTotal = PAYMENT_MODES.reduce((s, m) => s + (byMode[m] || 0), 0);

      // Cash reconciliation
      const cashCollected   = modeActive.reduce((s, b) => s + (Number(b.cash_amount) || 0), 0);
      const driverCommission = trackerEntries.reduce((s, te) => s + (Number(te.driver_commission) || 0), 0);
      const adjItems        = adjData || [];
      const adjNet          = adjItems.reduce((s, a) => s + (Number(a.amount) || 0), 0);

      // Block 1 lines (see utils/revenueBlocks). Balance (unpaid) = (total − tac) − paid over
      // billed bookings, whatever their status; cancelled receipts are outside Gross but real.
      const unpaidRemaining = revenueActive.reduce((s, b) => s + ((Number(b.total_amount) || 0) - (Number(b.tac) || 0) - paidOf(b)), 0);
      const cancelledReceipts = allBookings.filter(b => b.payment_status === 'Cancelled').reduce((s, b) => s + paidOf(b), 0);

      // Refunds — same receipts row set as the payments, so payment and refund are
      // counted together (or neither)
      const refundedBookings = modeActive.filter(b => b.is_refunded && Number(b.refund_amount) > 0);
      const totalRefunds     = refundedBookings.reduce((s, b) => s + (Number(b.refund_amount) || 0), 0);
      const cashRefunds      = refundedBookings.filter(b => b.refund_mode === 'Cash').reduce((s, b) => s + (Number(b.refund_amount) || 0), 0);

      // Non-cash refunds come off their mode; an unknown refund mode lands in Uncategorised
      // so the mode rows still sum to the total
      let uncategorised = 0;
      refundedBookings.forEach(b => {
        if (b.refund_mode === 'Cash') return;
        if (b.refund_mode && byMode[b.refund_mode] !== undefined) {
          byMode[b.refund_mode] -= Number(b.refund_amount) || 0;
        } else {
          uncategorised -= Number(b.refund_amount) || 0;
        }
      });

      const cashInHand   = cashCollected - driverCommission + adjNet - cashRefunds;
      // Net Collection = Payments Received − driver commission − refunds ± adjustments. Receipts
      // are net of TAC at source, so TAC is not deducted again. Identical by construction to
      // Block 1 (Gross → Net Collection) in utils/revenueBlocks.
      const netCollection = receivedTotal - driverCommission + adjNet - totalRefunds;

      setReport({
        paxSummary, gross, totalTAC, netCollection, receivedTotal,
        byMode, uncategorised, unpaidRemaining, cancelledReceipts,
        cashCollected, driverCommission, adjItems, adjNet, cashInHand,
        refundedBookings, totalRefunds,
        fromDate: from, toDate: to,
      });
    } finally {
      setLoading(false);
    }
  }, [CRUISE_ORDER]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-generate whenever dates are valid
  useEffect(() => {
    if (rangeValid) generateReport(fromDate, toDate);
  }, [fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectPreset = (preset) => {
    setActivePreset(preset.key);
    if (preset.key !== 'custom') {
      setFromDate(preset.from);
      setToDate(preset.to);
    }
  };

  // ─── Excel export ────────────────────────────────────────────────────────────
  const exportExcel = async () => {
    if (!report) return;
    try {
      const { default: ExcelJS } = await import('exceljs');
      const wb = new ExcelJS.Workbook();
      const fromDisp = toDisplayDate(report.fromDate);
      const toDisp   = toDisplayDate(report.toDate);
      const reportTitle = `Alfresco Grand — Range Report — ${formatDateForHeader(report.fromDate)} to ${formatDateForHeader(report.toDate)}`;
      const filename = `Range_Report_${fromDisp}_to_${toDisp}.xlsx`;
      const rupee = (n) => `₹${Math.round(n || 0)}`;

      const NAVY_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0A1628' } };
      const ALT_FILL  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7F5F1' } };
      const THIN = { style: 'thin', color: { argb: 'FFD0C8B8' } };
      const BORDER = { top: THIN, left: THIN, bottom: THIN, right: THIN };

      const addSheet = (name, colHeaders, dataRows) => {
        const ws = wb.addWorksheet(name);
        const r1 = ws.addRow([reportTitle]);
        r1.height = 24;
        r1.getCell(1).font = { bold: true, size: 11, color: { argb: 'FF0A1628' } };
        const r2 = ws.addRow([]);
        r2.height = 8;
        const r3 = ws.addRow(colHeaders);
        r3.height = 18;
        for (let c = 1; c <= colHeaders.length; c++) {
          const cell = r3.getCell(c);
          cell.fill = NAVY_FILL;
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 9 };
          cell.border = BORDER;
          cell.alignment = { vertical: 'middle', horizontal: 'left' };
        }
        dataRows.forEach((values, idx) => {
          const row = ws.addRow(values);
          row.height = 15;
          const useFill = idx % 2 === 1;
          for (let c = 1; c <= colHeaders.length; c++) {
            const cell = row.getCell(c);
            cell.border = BORDER;
            cell.alignment = { vertical: 'middle' };
            if (useFill) cell.fill = ALT_FILL;
          }
        });
        for (let c = 1; c <= colHeaders.length; c++) {
          let maxW = String(colHeaders[c - 1]).length;
          dataRows.forEach(row => { const len = String(row[c - 1] ?? '').length; if (len > maxW) maxW = len; });
          ws.getColumn(c).width = Math.min(maxW + 3, 55);
        }
        ws.views = [{ state: 'frozen', xSplit: 0, ySplit: 3, topLeftCell: 'A4' }];
        return ws;
      };

      const paxTotals = report.paxSummary.reduce(
        (s, r) => ({ b: s.b + r.bookings, a: s.a + r.adults, c: s.c + r.children, t: s.t + r.tl, p: s.p + r.pax }),
        { b: 0, a: 0, c: 0, t: 0, p: 0 }
      );
      addSheet('Pax Summary',
        ['Cruise', 'Deck', 'Bookings', 'Adults', 'Children', 'Tour Leaders', 'Total Pax'],
        [
          ...report.paxSummary.map(r => [r.cruise, r.deck, r.bookings, r.adults, r.children, r.tl, r.pax]),
          ['TOTAL', '', paxTotals.b, paxTotals.a, paxTotals.c, paxTotals.t, paxTotals.p],
        ]
      );

      const { block1, block2 } = buildRevenueBlocks(report);
      addSheet('Revenue Summary', ['Line', 'Amount', 'Running'], [
        ['— Gross Revenue to Net Collection —', '', ''],
        ...blockRowsForExport(block1, rupee),
        ['', '', ''],
        ['— Net Collection to Cash in Hand —', '', ''],
        ...blockRowsForExport(block2, rupee),
      ]);

      // Balance (unpaid) — this report's range only; the all-dates figure as a closing line
      const hasBal = balanceBookings.length > 0;
      addSheet('Balance (unpaid)',
        hasBal ? ['Date', 'Booking Name', 'Partner', 'Cruise', 'Deck', 'Adults', 'Net', 'Paid', 'Unpaid', 'Days Pending'] : ['Note'],
        [
          ...(hasBal
            ? [
                ...balanceBookings.map(b => [b.date, b.booking_name, b.partner_name || '', b.cruise_type, b.deck || '', b.adults || 0, rupee(b.net), b.paid ? rupee(b.paid) : '', rupee(b.remaining), balanceDaysLabel(balanceDaysPending(b.date))]),
                ['TOTAL UNPAID', '', '', '', '', '', '', '', rupee(balanceBookings.reduce((s, b) => s + b.remaining, 0)), ''],
              ]
            : [['No unpaid balances in this period']]),
          [`Outstanding across all dates (Balance bookings): ${rupee(allDatesOutstanding)} — see Partner Ledger`],
        ]
      );

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel export failed:', err);
    }
  };

  // ─── PDF export ──────────────────────────────────────────────────────────────
  const exportPDF = () => {
    if (!report) return;
    const fmtPDF = (n) => `Rs.${fmt(n)}`;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const fromDisp = toDisplayDate(report.fromDate);
    const toDisp   = toDisplayDate(report.toDate);

    doc.setFontSize(14);
    doc.setTextColor(10, 22, 40);
    doc.text('Alfresco Grand - Range Report', 10, 14);
    doc.setFontSize(9);
    doc.setTextColor(100, 90, 70);
    doc.text(`Period: ${fromDisp} to ${toDisp}`, 10, 21);

    const ML = 10, MR = 10;
    // Type scale (pt): was 7 head / 7 body / 7 section heads — too small on paper.
    const HEAD  = { fillColor: [10, 22, 40], textColor: 255, fontStyle: 'bold', fontSize: 7.5 };
    const BODY  = { fontSize: 8.5 };
    const STYLES = { overflow: 'linebreak', cellPadding: 1.2 };
    // Rows never split across pages; column headers repeat on continuation pages
    const BREAKS = { rowPageBreak: 'avoid', showHead: 'everyPage' };
    const ALT   = { fillColor: [249, 247, 243] };
    const MARGIN = { left: ML, right: MR };
    let y = 27;
    const gap = 5;
    const addSection = (title) => {
      if (y > 270) { doc.addPage(); y = 12; }
      doc.setFontSize(9.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(122, 106, 80);
      doc.text(title.toUpperCase(), ML, y);
      doc.setFont('helvetica', 'normal');
      y += 3;
    };

    addSection('Pax Summary');
    autoTable(doc, {
      startY: y,
      head: [['Cruise', 'Deck', 'Bookings', 'Adults', 'Children', 'TL', 'Total Pax']],
      body: [
        ...report.paxSummary.map(r => [r.cruise, r.deck, r.bookings, r.adults, r.children, r.tl, r.pax]),
        ['TOTAL', '',
          report.paxSummary.reduce((s,r)=>s+r.bookings,0),
          report.paxSummary.reduce((s,r)=>s+r.adults,0),
          report.paxSummary.reduce((s,r)=>s+r.children,0),
          report.paxSummary.reduce((s,r)=>s+r.tl,0),
          report.paxSummary.reduce((s,r)=>s+r.pax,0),
        ],
      ],
      headStyles: HEAD, bodyStyles: BODY, styles: STYLES,
      columnStyles: { 0:{cellWidth:52,halign:'left'}, 1:{cellWidth:28,halign:'left'}, 2:{cellWidth:16,halign:'right'}, 3:{cellWidth:16,halign:'right'}, 4:{cellWidth:18,halign:'right'}, 5:{cellWidth:14,halign:'right'}, 6:{cellWidth:46,halign:'right'} },
      alternateRowStyles: ALT, margin: MARGIN, ...BREAKS,
    });
    y = doc.lastAutoTable.finalY + gap;

    addSection('Revenue Summary');
    const { block1: pdfBlock1, block2: pdfBlock2 } = buildRevenueBlocks(report);
    const revRows = [
      ['Gross Revenue to Net Collection', '', ''],
      ...blockRowsForExport(pdfBlock1, fmtPDF, { minus: '-' }),
      ['', '', ''],
      ['Net Collection to Cash in Hand', '', ''],
      ...blockRowsForExport(pdfBlock2, fmtPDF, { minus: '-' }),
    ];
    autoTable(doc, {
      startY: y, head: [['Line', 'Amount', 'Running']], body: revRows,
      headStyles: HEAD, bodyStyles: BODY, styles: STYLES,
      columnStyles: { 0:{cellWidth:90,halign:'left'}, 1:{cellWidth:50,halign:'right',cellPadding:{top:1.5,right:10,bottom:1.5,left:1.5}}, 2:{cellWidth:50,halign:'right'} },
      alternateRowStyles: ALT, margin: MARGIN, ...BREAKS,
    });
    y = doc.lastAutoTable.finalY + gap;

    // Balance (unpaid) — this report's range only, unpaid remainder > 0
    if (balanceBookings.length > 0) {
      addSection('Balance (unpaid)');
      autoTable(doc, {
        startY: y,
        head: [['Date', 'Booking Name', 'Partner', 'Cruise', 'Deck', 'Adults', 'Net', 'Paid', 'Unpaid', 'Days']],
        body: [
          ...balanceBookings.map(b => [b.date, b.booking_name, b.partner_name || '', b.cruise_type, b.deck || '-', b.adults || 0, fmtPDF(b.net), b.paid ? fmtPDF(b.paid) : '', fmtPDF(b.remaining), balanceDaysLabel(balanceDaysPending(b.date))]),
          [{ content: 'TOTAL UNPAID', colSpan: 8 }, fmtPDF(balanceBookings.reduce((s, b) => s + b.remaining, 0)), ''],
        ],
        headStyles: HEAD, bodyStyles: BODY, styles: STYLES,
        // 190mm total (the page's usable width) — balanced for 8.5pt; long partner names wrap
        columnStyles: { 0:{cellWidth:20,halign:'left'}, 1:{cellWidth:32,halign:'left'}, 2:{cellWidth:30,halign:'left'}, 3:{cellWidth:22,halign:'left'}, 4:{cellWidth:18,halign:'left'}, 5:{cellWidth:12,halign:'right'}, 6:{cellWidth:17,halign:'right'}, 7:{cellWidth:13,halign:'right'}, 8:{cellWidth:17,halign:'right'}, 9:{cellWidth:9,halign:'right'} },
        alternateRowStyles: ALT, margin: MARGIN, ...BREAKS,
      });
      y = doc.lastAutoTable.finalY + 3;
    }
    if (y > 270) { doc.addPage(); y = 12; }
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(122, 106, 80);
    doc.text(`Outstanding across all dates (Balance bookings): ${fmtPDF(allDatesOutstanding)} - see Partner Ledger`, ML, y + 2);
    y += 6;

    const fromStr = report.fromDate.replace(/-/g, '');
    const toStr   = report.toDate.replace(/-/g, '');
    doc.save(`Range_Report_${fromStr}_to_${toStr}.pdf`);
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  const totalPaxSummary = report
    ? report.paxSummary.reduce((s, r) => ({ bookings: s.bookings + r.bookings, adults: s.adults + r.adults, children: s.children + r.children, tl: s.tl + r.tl, pax: s.pax + r.pax }), { bookings: 0, adults: 0, children: 0, tl: 0, pax: 0 })
    : null;

  const presetBtnStyle = (active) => ({
    padding: '8px 14px',
    background: active ? NAVY : '#fff',
    color: active ? '#fff' : 'var(--color-ink-secondary)',
    border: `1.5px solid ${active ? NAVY : 'var(--color-border-strong)'}`,
    borderRadius: 8,
    fontSize: '0.82rem',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background 0.12s, color 0.12s',
  });

  return (
    <div>
      <div className="back-row">
        <button className="back-btn" onClick={() => onNavigate('operations')}>← Operations</button>
      </div>

      <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: NAVY, marginBottom: 4 }}>Range Reports</h2>
      <p style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)', marginBottom: 20 }}>
        Multi-day financial summary. Partner Report and Bank Transfer Details are available in single-day Quick Reports.
      </p>

      {/* Preset selector */}
      <div style={card}>
        <div style={sectionLabel}>Report Period</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: activePreset === 'custom' ? 16 : 0 }}>
          {PRESETS.map(p => (
            <button key={p.key} style={presetBtnStyle(activePreset === p.key)} onClick={() => selectPreset(p)}>
              {p.label}
            </button>
          ))}
        </div>

        {activePreset === 'custom' && (
          <div style={{ marginTop: 4 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-ink-muted)' }}>From</span>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                style={{ background: 'var(--color-sunken)', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, padding: '9px 12px', color: NAVY, fontSize: '0.9rem', fontWeight: 600, outline: 'none', colorScheme: 'light' }} />
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-ink-muted)' }}>To</span>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                style={{ background: 'var(--color-sunken)', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, padding: '9px 12px', color: NAVY, fontSize: '0.9rem', fontWeight: 600, outline: 'none', colorScheme: 'light' }} />
            </div>
            {fromDate && toDate && fromDate > toDate && (
              <div style={{ fontSize: '0.78rem', color: '#dc2626', fontWeight: 600 }}>From date must be before To date.</div>
            )}
            {rangeExceeds31 && fromDate <= toDate && (
              <div style={{ fontSize: '0.78rem', color: '#dc2626', fontWeight: 600 }}>Maximum range is 31 days. Please select a shorter period.</div>
            )}
          </div>
        )}

        {activePreset !== 'custom' && report && !loading && (
          <div style={{ fontSize: '0.82rem', color: 'var(--color-ink-muted)', marginTop: 10 }}>
            {formatDateForHeader(report.fromDate)}{report.fromDate !== report.toDate ? ` — ${formatDateForHeader(report.toDate)}` : ''}
          </div>
        )}

        {loading && (
          <div style={{ fontSize: '0.82rem', color: GOLD, fontWeight: 600, marginTop: 12 }}>Generating report…</div>
        )}
      </div>

      {report && !loading && (
        <>
          {/* Export buttons */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
            <button onClick={exportExcel}
              style={{ padding: '9px 20px', background: '#166534', color: '#fff', border: 'none', borderRadius: 9, fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>
              Export Excel (.xlsx)
            </button>
            <button onClick={exportPDF}
              style={{ padding: '9px 20px', background: '#991b1b', color: '#fff', border: 'none', borderRadius: 9, fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>
              Export PDF
            </button>
          </div>

          {/* Pax Summary */}
          <div style={card}>
            <SectionTitle title="Pax Summary" />
            <div style={tableWrap}>
              <table className="data-table" style={tblStyle}>
                <thead>
                  <tr>
                    {['Cruise', 'Deck', 'Bookings', 'Adults', 'Children', 'TL', 'Total Pax'].map(h => (
                      <th key={h} style={{ ...thStyle, textAlign: ['Bookings','Adults','Children','TL','Total Pax'].includes(h) ? 'right' : 'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.paxSummary.map((r, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>{r.cruise}</td>
                      <td style={tdStyle}>{r.deck}</td>
                      <td style={tdNumStyle}>{r.bookings}</td>
                      <td style={tdNumStyle}>{r.adults}</td>
                      <td style={tdNumStyle}>{r.children}</td>
                      <td style={tdNumStyle}>{r.tl}</td>
                      <td style={{ ...tdNumStyle, fontWeight: 800, color: NAVY }}>{r.pax}</td>
                    </tr>
                  ))}
                </tbody>
                {totalPaxSummary && (
                  <tfoot>
                    <tr>
                      <td style={{ ...tfootStyle, textAlign: 'left', paddingLeft: 12 }} colSpan={2}>TOTAL</td>
                      <td style={tfootStyle}>{totalPaxSummary.bookings}</td>
                      <td style={tfootStyle}>{totalPaxSummary.adults}</td>
                      <td style={tfootStyle}>{totalPaxSummary.children}</td>
                      <td style={tfootStyle}>{totalPaxSummary.tl}</td>
                      <td style={{ ...tfootStyle, color: GOLD, fontSize: '0.9rem' }}>{totalPaxSummary.pax}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Revenue Summary */}
          <div style={card}>
            <SectionTitle title="Revenue Summary" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 16 }}>
              {[
                { label: 'Gross Revenue',  value: fmtAmount(report.gross),          color: NAVY },
                { label: 'TAC at source',  value: fmtAmount(report.totalTAC),        color: '#92400e' },
                { label: 'Net Collection', value: fmtAmount(report.netCollection),   color: '#166534' },
                { label: 'Balance (unpaid)', value: fmtAmount(report.unpaidRemaining), color: report.unpaidRemaining > 0.5 ? '#dc2626' : '#6b7280' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: 'var(--color-sunken)', borderRadius: 10, padding: '12px 16px' }}>
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5 }}>{label}</div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 800, color, wordBreak: 'break-all' }}>{value}</div>
                </div>
              ))}
            </div>

            {/* Two rolling subtractions — Gross to Net Collection, then down to the drawer */}
            {(() => {
              const { block1, block2 } = buildRevenueBlocks(report);
              return (
                <div style={{ display: 'grid', gap: 14, maxWidth: 640 }}>
                  <RevenueBlockTable title="Gross Revenue to Net Collection" rows={block1} />
                  <RevenueBlockTable title="Net Collection to Cash in Hand" rows={block2} resultTone={report.cashInHand >= 0 ? '#166534' : '#dc2626'} />
                </div>
              );
            })()}
          </div>

        </>
      )}

      {/* Balance (unpaid) — this report's range, real-time; all-dates figure below */}
      {report && (
        <div style={card}>
          <SectionTitle title="Balance (unpaid)" />
          {balanceBookings.length === 0 ? (
            <p style={{ fontSize: '0.82rem', color: 'var(--color-ink-faint)', textAlign: 'center', padding: '10px 0 14px' }}>No unpaid balances in this period.</p>
          ) : (
            <>
              <p style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)', marginBottom: 14 }}>
                Bookings in this period with an unpaid remainder — the Balance (unpaid) line above, itemised. Click a row to open in Daily Tracker.
              </p>
              <div style={tableWrap}>
                <table className="data-table" style={tblStyle}>
                  <thead>
                    <tr>
                      {['Date', 'Booking', 'Cruise', 'Deck', 'Adults', 'Net', 'Paid', 'Unpaid', 'Days Pending'].map(h => (
                        <th key={h} style={{ ...thStyle, textAlign: ['Adults', 'Net', 'Paid', 'Unpaid', 'Days Pending'].includes(h) ? 'right' : 'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {balanceBookings.map((b) => {
                      const daysPending = balanceDaysPending(b.date);
                      return (
                        <tr key={b.id}
                          onClick={() => onNavigate('daily-tracker', b.date, { cruise_type: b.cruise_type })}
                          style={{ cursor: 'pointer' }}
                        >
                          <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{b.date}</td>
                          <td style={tdStyle}>
                            <div style={{ fontWeight: 600 }}>{b.booking_name}</div>
                            {/* Partner line only when there is one — direct guests get no placeholder */}
                            {b.partner_name && <div style={{ fontSize: '0.74rem', fontWeight: 600, color: '#1e40af', marginTop: 1 }}>{b.partner_name}</div>}
                          </td>
                          <td style={tdStyle}>{b.cruise_type}</td>
                          <td style={tdStyle}>{b.deck || '—'}</td>
                          <td style={tdNumStyle}>{b.adults || 0}</td>
                          <td style={tdNumStyle}>{fmtAmount(b.net)}</td>
                          <td style={{ ...tdNumStyle, color: b.paid ? '#166534' : 'var(--color-ink-faint)' }}>{b.paid ? fmtAmount(b.paid) : ''}</td>
                          <td style={{ ...tdNumStyle, color: '#1e40af', fontWeight: 700 }}>{fmtAmount(b.remaining)}</td>
                          <td style={{ ...tdNumStyle, color: daysPending > 2 ? '#dc2626' : '#92400e' }}>{balanceDaysLabel(daysPending)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={{ ...tfootStyle, textAlign: 'left', paddingLeft: 12 }} colSpan={7}>TOTAL UNPAID</td>
                      <td style={{ ...tfootStyle, color: '#1e40af' }}>{fmtAmount(balanceBookings.reduce((s, b) => s + b.remaining, 0))}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
          {/* The full picture, one figure — the table above stays reconcilable to this period */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 6, padding: '10px 12px', background: 'var(--color-sunken)', borderRadius: 8 }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)' }}>
              Outstanding across all dates (Balance bookings)
              <span style={{ marginLeft: 10, fontWeight: 700, color: allDatesOutstanding > 0.5 ? '#1e40af' : 'var(--color-ink-faint)', fontVariantNumeric: 'tabular-nums' }}>{fmtAmount(allDatesOutstanding)}</span>
            </span>
            <button onClick={() => onNavigate('partner-ledger')}
              style={{ fontSize: '0.78rem', fontWeight: 700, color: GOLD, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              Open Partner Ledger →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
